import axios from "axios";
import { useEffect, useRef, useState } from "react";
import { X, LogOut } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

axios.defaults.withCredentials = true;

const formatApiErrorDetail = (detail) => {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
};

const GoogleMark = () => (
  <svg width="16" height="16" viewBox="0 0 48 48"><path fill="#FFC107" d="M43.6 20.1H42V20H24v8h11.3C33.7 32.7 29.2 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.9 1.2 8 3l5.7-5.7C34 6.1 29.3 4 24 4 13 4 4 13 4 24s9 20 20 20 20-9 20-20c0-1.3-.1-2.6-.4-3.9z"/><path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.9 1.2 8 3l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z"/><path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35.1 26.7 36 24 36c-5.2 0-9.6-3.3-11.3-8l-6.5 5C9.5 39.6 16.2 44 24 44z"/><path fill="#1976D2" d="M43.6 20.1H42V20H24v8h11.3c-.8 2.2-2.2 4.2-4.1 5.6l6.2 5.2C36.9 39.2 44 34 44 24c0-1.3-.1-2.6-.4-3.9z"/></svg>
);

export const AuthModal = ({ onClose, onAuth }) => {
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const forgotSubmit = async (e) => {
    e.preventDefault();
    setBusy(true); setError("");
    try {
      await axios.post(`${API}/auth/forgot-password`, { email: form.email, origin: window.location.origin });
      setSent(true);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setError("");
    try {
      const payload = mode === "login" ? { email: form.email, password: form.password } : form;
      const { data } = await axios.post(`${API}/auth/${mode === "login" ? "login" : "register"}`, payload);
      toast.success(mode === "login" ? "Welcome back" : "Account created", { description: `Signed in as ${data.name || data.email}` });
      onAuth(data);
      onClose();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="modal-backdrop" data-testid="auth-modal">
      <div className="modal-card">
        <button className="close-btn" onClick={onClose} data-testid="close-auth-button"><X size={20}/></button>
        <span className="eyebrow">Your Jamvanya account</span>
        <h2>{mode === "forgot" ? "Reset it." : mode === "login" ? "Welcome back." : "Join Jamvanya."}</h2>
        <p className="modal-intro">{mode === "forgot" ? "Enter your account email and we'll send you a reset link." : mode === "login" ? "Sign in to see your orders and check out faster." : "Create a profile to track orders and reorder in a tap."}</p>
        {mode !== "forgot" && <button type="button" className="google-btn" onClick={googleLogin} data-testid="google-login-button"><GoogleMark/> Continue with Google</button>}
        {mode !== "forgot" && <div className="auth-divider">or with email</div>}
        {mode !== "forgot" && (
        <div className="auth-tabs">
          <button type="button" className={mode === "login" ? "active" : ""} onClick={() => { setMode("login"); setError(""); }} data-testid="auth-tab-login">Login</button>
          <button type="button" className={mode === "register" ? "active" : ""} onClick={() => { setMode("register"); setError(""); }} data-testid="auth-tab-register">Create account</button>
        </div>
        )}
        {mode === "forgot" ? (
          sent ? (
            <p className="modal-intro" data-testid="forgot-sent-message">If an account exists for this email, a reset link is on its way. It expires in 30 minutes.</p>
          ) : (
          <form onSubmit={forgotSubmit} data-testid="forgot-form">
            <input required type="email" placeholder="Your account email" value={form.email} onChange={e => setForm({...form, email: e.target.value})} data-testid="forgot-email-input"/>
            {error && <div className="auth-error" data-testid="auth-error-message">{error}</div>}
            <button className="button button-primary full" disabled={busy} data-testid="forgot-submit-button">{busy ? "Sending…" : "Send reset link"}</button>
          </form>
          )
        ) : (
        <form onSubmit={submit} data-testid="auth-form">
          {mode === "register" && <input required placeholder="Your name" value={form.name} onChange={e => setForm({...form, name: e.target.value})} data-testid="auth-name-input"/>}
          <input required type="email" placeholder="Email address" value={form.email} onChange={e => setForm({...form, email: e.target.value})} data-testid="auth-email-input"/>
          <input required type="password" minLength={8} placeholder="Password (min 8 characters)" value={form.password} onChange={e => setForm({...form, password: e.target.value})} data-testid="auth-password-input"/>
          {error && <div className="auth-error" data-testid="auth-error-message">{error}</div>}
          <button className="button button-primary full" disabled={busy} data-testid="auth-submit-button">{busy ? "Please wait…" : mode === "login" ? "Login" : "Create account"}</button>
          {mode === "login" && <button type="button" className="auth-link" onClick={() => { setMode("forgot"); setError(""); setSent(false); }} data-testid="forgot-password-link">Forgot password?</button>}
        </form>
        )}
        {mode === "forgot" && <button type="button" className="auth-link" onClick={() => { setMode("login"); setError(""); }} data-testid="back-to-login-link">Back to login</button>}
      </div>
    </div>
  );
};

export const ProfileModal = ({ user, onClose, onLogout }) => {
  const logout = async () => {
    try { await axios.post(`${API}/auth/logout`, {}); } catch {}
    onLogout();
    onClose();
    toast.success("Signed out", { description: "See you soon." });
  };
  return (
    <div className="modal-backdrop" data-testid="profile-modal">
      <div className="modal-card">
        <button className="close-btn" onClick={onClose} data-testid="close-profile-button"><X size={20}/></button>
        <span className="eyebrow">Your profile</span>
        <h2>Namaste{user.name ? `, ${user.name.split(" ")[0]}` : ""}.</h2>
        <div className="profile-head">
          <span className="profile-avatar" data-testid="profile-avatar">{(user.name || user.email || "J").charAt(0).toUpperCase()}</span>
          <div>
            <b data-testid="profile-name">{user.name || "Jamvanya customer"}</b>
            <p className="modal-intro" data-testid="profile-email">{user.email}</p>
          </div>
        </div>
        {user.role === "admin" && <a className="button button-primary full" href="/admin" style={{ marginBottom: 10 }} data-testid="admin-dashboard-link">Open admin dashboard</a>}
        <button className="button button-primary full" onClick={logout} data-testid="logout-button"><LogOut size={15}/> Log out</button>
      </div>
    </div>
  );
};

export const ResetPassword = ({ token, onDone }) => {
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password !== confirm) { setError("Passwords do not match"); return; }
    setBusy(true);
    try {
      await axios.post(`${API}/auth/reset-password`, { token, password });
      toast.success("Password updated", { description: "Log in with your new password." });
      onDone(true);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setBusy(false); }
  };

  return (
    <div className="modal-backdrop" data-testid="reset-password-modal">
      <div className="modal-card">
        <button className="close-btn" onClick={() => onDone(false)} data-testid="close-reset-button"><X size={20}/></button>
        <span className="eyebrow">Almost there</span>
        <h2>Choose a new password.</h2>
        <p className="modal-intro">At least 8 characters. You'll use it to log in from now on.</p>
        <form onSubmit={submit} data-testid="reset-password-form">
          <input required type="password" minLength={8} placeholder="New password (min 8 characters)" value={password} onChange={e => setPassword(e.target.value)} data-testid="reset-password-input"/>
          <input required type="password" minLength={8} placeholder="Confirm new password" value={confirm} onChange={e => setConfirm(e.target.value)} data-testid="reset-password-confirm-input"/>
          {error && <div className="auth-error" data-testid="auth-error-message">{error}</div>}
          <button className="button button-primary full" disabled={busy} data-testid="reset-submit-button">{busy ? "Updating…" : "Update password"}</button>
        </form>
      </div>
    </div>
  );
};

export const AuthCallback = ({ onDone }) => {
  const processed = useRef(false);
  useEffect(() => {
    if (processed.current) return;
    processed.current = true;
    const sessionId = window.location.hash.split("session_id=")[1]?.split("&")[0];
    axios.post(`${API}/auth/session`, { session_id: sessionId })
      .then((res) => { window.history.replaceState(null, "", window.location.pathname); onDone(res.data); })
      .catch(() => { window.history.replaceState(null, "", window.location.pathname); onDone(false); });
  }, [onDone]);
  return <div className="auth-callback" data-testid="auth-callback">Signing you in…</div>;
};
